import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { Card, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera, faVideoSlash } from '@fortawesome/free-solid-svg-icons';

interface ScannerProps {
  onScan: (result: string) => void;
}

interface Camera {
  id: string;
  label: string;
}

const Scanner: React.FC<ScannerProps> = ({ onScan }) => {
    const [cameras, setCameras] = useState<Camera[]>([]);
    const [currentCamera, setCurrentCamera] = useState<string>('');
    const scannerRef = useRef<Html5Qrcode | null>(null);
    const isScanning = useRef<boolean>(false);
    const scannerContainerId = 'scanner-container';

    const stopScanner = useCallback(async () => {
        try {
            if (scannerRef.current && isScanning.current) {
                await scannerRef.current.stop();
                isScanning.current = false;
            }
        } catch (err) {
            console.error('Error stopping scanner:', err);
        }
    }, []);

    const clearScanner = useCallback(async () => {
        try {
            await stopScanner();
            const scannerElement = document.getElementById(scannerContainerId);
            if (scannerElement) {
                scannerElement.innerHTML = '';
            }
            if (scannerRef.current) {
                await scannerRef.current.clear();
                scannerRef.current = null;
            }
        } catch (err) {
            console.error('Error clearing scanner:', err);
        }
    }, [stopScanner]);

    const handleScan = useCallback(async (decodedText: string) => {
        try {
            await clearScanner();
            onScan(decodedText);
        } catch (err) {
            console.error('Error handling scan:', err);
        }
    }, [clearScanner, onScan]);

    const startScanner = useCallback(async (cameraId: string) => {
        try {
            await clearScanner();

            scannerRef.current = new Html5Qrcode(scannerContainerId, {
                formatsToSupport: [
                    Html5QrcodeSupportedFormats.QR_CODE,
                    Html5QrcodeSupportedFormats.EAN_13,
                    Html5QrcodeSupportedFormats.CODE_128,
                    Html5QrcodeSupportedFormats.CODE_39,
                    Html5QrcodeSupportedFormats.UPC_A,
                    Html5QrcodeSupportedFormats.UPC_E,
                    Html5QrcodeSupportedFormats.EAN_8,
                ],
                verbose: false
            });

            await scannerRef.current.start(
                cameraId,
                {
                    fps: 10,
                    qrbox: { width: 250, height: 250 },
                    aspectRatio: 1.0
                },
                handleScan,
                (errorMessage) => {
                    if (!errorMessage.includes('No MultiFormat Readers') && 
              !errorMessage.includes('No barcode or QR code detected')) {
                        console.log(errorMessage);
                    }
                }
            );
            isScanning.current = true;
        } catch (err) {
            console.error('Error starting scanner:', err);
        }
    }, [clearScanner, handleScan]);

    useEffect(() => {
        const init = async () => {
            try {
                const devices = await Html5Qrcode.getCameras();
                setCameras(devices);
        
                if (devices.length > 0) {
                    const backCamera = devices.find(device => 
                        device.label.toLowerCase().includes('back') || 
            device.label.toLowerCase().includes('environment')
                    );
                    const cameraId = backCamera?.id || devices[0].id;
                    setCurrentCamera(cameraId);
                    await startScanner(cameraId);
                }
            } catch (err) {
                console.error('Error initializing scanner:', err);
            }
        };

        init();

        return () => {
            clearScanner().catch(console.error);
        };
    }, [clearScanner, startScanner]);

    const switchCamera = async () => {
        const currentIndex = cameras.findIndex(camera => camera.id === currentCamera);
        const nextIndex = (currentIndex + 1) % cameras.length;
        const newCameraId = cameras[nextIndex].id;
        setCurrentCamera(newCameraId);
        await startScanner(newCameraId);
    };

    if(!currentCamera && !isScanning) {
        return <Card className="border-0 shadow-sm">
            <Card.Body className='text-center'>
                <h4>No Camera detected.</h4>
                <FontAwesomeIcon icon={faVideoSlash} className='text-danger my-4' size='xl'/>
                <p>If you have a camera connected, please check that your browser has permission to use your camera.</p>
            </Card.Body>
        </Card>;
    }
    else {
        return (
            <Card className="border-0 shadow-sm">
                <Card.Body>
                    <div 
                        id={scannerContainerId}
                        style={{ width: '100%', minHeight: '300px' }}
                    />
                    {cameras.length > 1 && (
                        <Button
                            variant="outline-primary"
                            onClick={switchCamera}
                            className="mt-3 d-flex align-items-center justify-content-center gap-2"
                        >
                            <FontAwesomeIcon icon={faCamera} />
                Switch Camera
                        </Button>
                    )}
                </Card.Body>
            </Card>
        );
    }
};

export default Scanner;
